import React from 'react';
import { AIMessage } from './AIMessage';
import { UserMessage } from './UserMessage';
import { ToolCallIndicator } from './ToolCallIndicator';
import { AdMessage } from './AdMessage';
import { chatLogger } from '../../utils/logger';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'ad';
  content: string;
  parts?: any[];
  timestamp: Date;
  adId?: string; // Only for ad messages - the div ID
}

interface MessageRendererProps {
  message: Message;
  isLoading?: boolean;
  projectIconUrl?: string | null;
  projectId: string;
  threadId: string;
}

export function MessageRenderer({ message, isLoading = false, projectIconUrl, projectId, threadId }: MessageRendererProps) {
  // For user messages, just render the UserMessage component
  if (message.role === 'user') {
    return (
      <UserMessage
        content={message.content}
        timestamp={message.timestamp}
      />
    );
  }

  // For ad messages, render the AdMessage component
  if (message.role === 'ad') {
    return (
      <AdMessage />
    );
  }

  // For assistant messages, determine what to show
  const parts = message.parts || [];
  
  // Check for active tool calls in AI SDK v5 format
  const hasActiveTool = parts.some(
    part => (part.type?.startsWith('tool-') || part.type === 'dynamic-tool') && 
    part.state && part.state !== 'output-available'
  );
  
  const hasTextContent = message.content || parts.some(part => part.type === 'text');

  // If we have active tools and no text content yet, show research indicator
  if (hasActiveTool && !hasTextContent && !isLoading) {
    const activeTool = parts.find(
      part => (part.type?.startsWith('tool-') || part.type === 'dynamic-tool') && 
      part.state && part.state !== 'output-available'
    );
    
    return (
      <ToolCallIndicator
        toolName={activeTool.toolName || activeTool.type}
        state={activeTool.state}
        args={activeTool.input}
      />
    );
  }

  // Otherwise, show the AI response (with loading state if needed)
  if (hasTextContent || isLoading) {
    return (
      <AIMessage
        content={message.content}
        parts={parts}
        timestamp={message.timestamp}
        isLoading={isLoading}
        projectIconUrl={projectIconUrl}
        projectId={projectId}
        threadId={threadId}
        messages={[]} // We don't have access to the full messages array here, so pass empty array
      />
    );
  }

  return null;
} 